/**
 * SocialPreneur — "Asta Cafe" bespoke landing (Layer-3 tenant override of
 * `@app/storefront/tenant-landing`). A faithful recreation of the handoff
 * (`client design handoffs/design_handoff_socialpreneur/Asta Cafe Landing.dc.html`):
 * a premium, content-first marketing page for SocialPreneur Inc.'s Asta Cafe
 * wellness-coffee blend — utility bar, gold sticky header, hero, The Blend,
 * an infographic Gallery carousel, seven ingredients, the Astaxanthin flagship
 * feature, How to Use, Certifications, an FAQ, a CTA band, and the dark footer.
 *
 * LANDING ONLY: the handoff's three ingredient deep-dive pages (Astaxanthin /
 * Coffee / Raw Brown Sugar) are intentionally DROPPED — their cards carry no
 * "→" link, and the feature's "Explore the research" CTA points at #gallery.
 * So `renderTenantPage` returns null and `TENANT_ROUTES` is empty (Mhyel's shape).
 *
 * This is the BLESSED folder use — a landing has no business logic, so it can't
 * drift against core. The gold/espresso look is owned here (see `tokens.ts` /
 * `asta.css`); the runtime tenant theme (SocialPreneur green) drives the member
 * portal + the shared shop surfaces, which stay green. NOTHING on this page
 * routes into the shop or signup: SocialPreneur runs it as an information site,
 * so "Where to Buy" scrolls to the Contact band and the only action is a mailto.
 *
 * The seam `home.tsx` / `App.tsx` consume is FOUR exports — `HAS_BESPOKE_LANDING`,
 * `TenantLanding`, `renderTenantPage`, `TENANT_ROUTES`. They are NOT optional: a
 * tenant folder that omits any breaks THIS tenant's build at the
 * `@app/storefront/tenant-landing` import (`tsc --noEmit`).
 */
import {
  type LucideIcon,
  ShieldCheck,
  Coffee,
  Leaf,
  Sparkles,
  Sprout,
  FlaskConical,
  Soup,
  Milk,
  Brain,
  HeartPulse,
  Eye,
  Activity,
  Flame,
  Moon,
  Check,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';
import { SOCIALPRENEUR_SITE } from '@mlm/shared';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { renderInline, renderLines } from '../../../storefront/lib/rich-paras';
import { AstaShell, scrollToAnchor } from './shell';
import { AstaAbout } from './about';
import { GalleryCarousel } from './carousel';
import { FONT_DISPLAY, FONT_BODY, FONT_SCRIPT } from './tokens';

export const HAS_BESPOKE_LANDING = true;

export function TenantLanding(): JSX.Element {
  return (
    <AstaShell>
      <AstaHome />
    </AstaShell>
  );
}

// Landing-only: no bespoke non-home pages, no extra routes.
/** Bespoke pages for non-home paths. Routed via TENANT_ROUTES (build-time), so
 * they mount regardless of the tenant's stored layout — including in
 * information-site-only mode, where App.tsx serves `/` + TENANT_ROUTES only.
 *
 * `/about-us`, NOT `/about`: App.tsx's RESERVED_PATHS already owns `/about`
 * (along with `/faq`, `/shop`, `/contact`…) with a hardcoded route to the SHARED
 * Omni CMS page, and it filters those paths straight out of TENANT_ROUTES — so a
 * bespoke page parked on one is silently dropped and visitors land on the green
 * marketplace About page instead. agp and upmi both use `/about-us` for the same
 * reason. Check RESERVED_PATHS before adding any path here. */
export function renderTenantPage(path: string): JSX.Element | null {
  if (path === '/about-us') return <AstaAbout />;
  return null;
}
export const TENANT_ROUTES = ['/about-us'];

// ── Content ──────────────────────────────────────────────────────────────────
// Copy lives in the site-content manifest (`SOCIALPRENEUR_SITE` in @mlm/shared,
// handoff copy verbatim as defaults; admin-editable via Storefront → Site
// Pages). Icons + accent colors are CODE-OWNED here, paired with the manifest
// rows BY INDEX — those lists are `fixedLength` so the editor can't break the
// pairing.
type Icon = LucideIcon;

const PILLAR_ICONS: Icon[] = [ShieldCheck, Coffee, Leaf];

const INGREDIENT_META: { Icon: Icon; accent: string }[] = [
  { Icon: Sparkles, accent: '#F0787F' },
  { Icon: Sprout, accent: '#C79BE6' },
  { Icon: FlaskConical, accent: '#F0C46A' },
  { Icon: Leaf, accent: '#8FD07A' },
  { Icon: Coffee, accent: '#D6A876' },
  { Icon: Soup, accent: '#E0A96A' },
  { Icon: Milk, accent: '#9DC4DE' },
];

const BENEFIT_ICONS: Icon[] = [Brain, HeartPulse, Eye, Activity, Flame];

const CERT_ICONS: Icon[] = [Moon, Check];

// A user-fillable photo slot: renders the real image when supplied (tokens.IMG),
// else a styled placeholder frame with a faint icon + caption. `fit` is 'cover'
// for photos that should fill the frame, 'contain' for a transparent product
// cutout that should show whole. TODO(asta-assets).
function ImageSlot({
  src,
  label,
  Icon,
  fit = 'cover',
}: {
  src: string | null;
  label: string;
  Icon: Icon;
  fit?: 'cover' | 'contain';
}) {
  if (src) {
    return (
      <img
        src={src}
        alt={label}
        style={{ width: '100%', height: '100%', objectFit: fit, display: 'block', padding: fit === 'contain' ? 18 : 0 }}
      />
    );
  }
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
        textAlign: 'center',
        color: 'var(--muted)',
      }}
    >
      <Icon size={34} strokeWidth={1.4} style={{ opacity: 0.5 }} />
      <span style={{ fontFamily: FONT_DISPLAY, fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', opacity: 0.7 }}>
        {label}
      </span>
    </div>
  );
}

function AstaHome(): JSX.Element {
  const sc = useSiteContent(SOCIALPRENEUR_SITE);
  const faqs = sc.items('landing.faq.items');
  const contactEmail = sc.text('landing.cta.contactEmail').trim();
  return (
    <div style={{ fontFamily: FONT_BODY }}>
      {/* ── Hero ── */}
      <section
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'radial-gradient(120% 90% at 70% 20%, #2c1c0d 0%, #1a1208 45%, #120c06 100%)',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
          <div
            className="asta-glow"
            style={{
              position: 'absolute',
              left: '66%',
              top: '38%',
              width: 760,
              height: 760,
              background: 'radial-gradient(circle, rgba(200,148,42,.30), rgba(140,70,24,.12) 42%, transparent 66%)',
              animation: 'ac-glow 7s ease-in-out infinite',
            }}
          />
        </div>
        <div
          style={{
            position: 'relative',
            maxWidth: 1200,
            margin: '0 auto',
            padding: '0 24px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))',
            gap: 36,
            alignItems: 'center',
            minHeight: 640,
          }}
        >
          <div style={{ padding: '64px 0' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                fontFamily: FONT_DISPLAY,
                fontWeight: 600,
                fontSize: 12.5,
                letterSpacing: '.24em',
                textTransform: 'uppercase',
                color: 'var(--gold)',
                border: '1px solid var(--line)',
                borderRadius: 999,
                padding: '8px 16px',
              }}
            >
              {sc.text('landing.hero.badge')}
            </div>
            <h1
              style={{
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(40px,6vw,74px)',
                lineHeight: 0.98,
                letterSpacing: '.005em',
                textTransform: 'uppercase',
                margin: '22px 0 0',
                color: 'var(--cream)',
              }}
            >
              {renderLines(sc.text('landing.hero.title'))}
              <br />
              <span style={{ color: 'var(--gold)' }}>{sc.text('landing.hero.titleAccent')}</span>
            </h1>
            <p style={{ maxWidth: '46ch', margin: '22px 0 0', fontSize: 17, lineHeight: 1.75, color: 'var(--muted)' }}>
              {sc.text('landing.hero.sub')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 32 }}>
              <a
                href="#blend"
                onClick={scrollToAnchor('#blend')}
                className="asta-btn-gold"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 9,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: '#2a1b0e',
                  background: 'var(--gold-grad)',
                  padding: '15px 26px',
                  borderRadius: 11,
                  boxShadow: '0 8px 22px rgba(200,148,42,.32)',
                }}
              >
                {sc.text('landing.hero.ctaPrimary')} <ArrowRight size={15} strokeWidth={2.4} />
              </a>
              <a
                href="#astaxanthin"
                onClick={scrollToAnchor('#astaxanthin')}
                className="asta-btn-outline"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 9,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 500,
                  fontSize: 15,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--cream)',
                  background: 'transparent',
                  padding: '15px 26px',
                  borderRadius: 11,
                  border: '1px solid var(--line)',
                }}
              >
                {sc.text('landing.hero.ctaSecondary')}
              </a>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 34 }}>
              {sc.paras('landing.hero.chips').map((c) => (
                <span
                  key={c}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: 'var(--muted)',
                    background: 'rgba(255,255,255,.03)',
                    border: '1px solid var(--line)',
                    borderRadius: 999,
                    padding: '7px 13px',
                  }}
                >
                  <i style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--gold)', display: 'block' }} />
                  {c}
                </span>
              ))}
            </div>
          </div>
          <div style={{ position: 'relative', padding: '40px 0', display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                position: 'absolute',
                bottom: 34,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '74%',
                height: 34,
                background: 'radial-gradient(ellipse, rgba(0,0,0,.55), transparent 70%)',
                filter: 'blur(4px)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 26,
                pointerEvents: 'none',
              }}
            >
              {[
                { h: 70, dur: '3.4s', delay: '0s' },
                { h: 88, dur: '3.9s', delay: '.6s' },
                { h: 64, dur: '3.2s', delay: '1.1s' },
              ].map((s, i) => (
                <i
                  key={i}
                  className="asta-steam"
                  style={{
                    width: 5,
                    height: s.h,
                    borderRadius: 999,
                    background: 'linear-gradient(to top,rgba(245,239,224,.5),transparent)',
                    animation: `ac-steam ${s.dur} ease-in-out infinite ${s.delay}`,
                  }}
                />
              ))}
            </div>
            <div
              style={{
                position: 'relative',
                width: 'min(400px,82%)',
                aspectRatio: '4/5',
                borderRadius: 18,
                border: '1px solid var(--line)',
                background: 'linear-gradient(160deg,#241708,#160e06)',
                boxShadow: '0 30px 60px rgba(0,0,0,.5)',
                overflow: 'hidden',
              }}
            >
              <ImageSlot src={sc.img('landing.hero.productImage')} label="Product photo coming soon" Icon={Coffee} fit="contain" />
              <div
                style={{
                  position: 'absolute',
                  top: 14,
                  left: 14,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: '.18em',
                  textTransform: 'uppercase',
                  color: 'var(--gold-l)',
                  background: 'rgba(14,10,5,.6)',
                  border: '1px solid var(--line)',
                  borderRadius: 999,
                  padding: '6px 12px',
                  pointerEvents: 'none',
                }}
              >
                {sc.text('landing.hero.boxBadge')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── The Blend (cream) ── */}
      <section id="blend" style={{ scrollMarginTop: 96, background: 'var(--cream-2)', color: 'var(--espresso)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '88px 24px' }}>
          <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto' }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.24em', textTransform: 'uppercase', color: 'var(--gold-d)' }}>
              {sc.text('landing.blend.eyebrow')}
            </div>
            <div style={{ fontFamily: FONT_SCRIPT, fontSize: 'clamp(40px,6vw,66px)', lineHeight: 1, color: 'var(--gold-d)', marginTop: 12 }}>
              {sc.text('landing.blend.script')}
            </div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(30px,4.4vw,50px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '2px 0 0', color: 'var(--espresso)' }}>
              {sc.text('landing.blend.title')}
            </h2>
            <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--muted-2)', margin: '20px auto 0', maxWidth: '60ch' }}>
              {sc.text('landing.blend.body')}
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 22, marginTop: 52 }}>
            {sc.items('landing.blend.pillars').map((p, i) => (
              <div key={p.title} style={{ background: '#fff', border: '1px solid #efe4d2', borderRadius: 16, padding: '30px 26px', boxShadow: '0 10px 30px rgba(90,60,20,.06)' }}>
                <span
                  style={{
                    width: 58,
                    height: 58,
                    borderRadius: 15,
                    background: 'linear-gradient(150deg,#FBF0D6,#F0DBA6)',
                    color: 'var(--gold-d)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'inset 0 0 0 1px rgba(200,148,42,.25)',
                  }}
                >
                  {(() => {
                    const PIcon = PILLAR_ICONS[i] ?? ShieldCheck;
                    return <PIcon size={28} strokeWidth={1.7} />;
                  })()}
                </span>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 20, letterSpacing: '.05em', textTransform: 'uppercase', margin: '20px 0 8px', color: 'var(--espresso)' }}>
                  {p.title}
                </h3>
                <p style={{ fontSize: 14.5, lineHeight: 1.65, color: 'var(--muted-2)', margin: 0 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Products (dark) — showcase-only, hidden until the client adds
             slides via Site Pages (landing.products.slides). Display style is
             a Site Pages choice: big carousel OR catalog-style card grid. ── */}
      {(() => {
        const productSlides = sc.items('landing.products.slides').map((s) => ({
          src: sc.src(s.imageKey ?? ''),
          tag: s.tag ?? '',
          title: s.title ?? '',
        }));
        if (productSlides.length === 0) return null;
        const asGrid = sc.text('landing.products.display') === 'grid';
        return (
          <section
            id="products"
            style={{ scrollMarginTop: 80, background: 'linear-gradient(180deg,#1d130a,#160e06)', borderTop: '1px solid var(--line)' }}
          >
            <div style={{ maxWidth: asGrid ? 1200 : 1120, margin: '0 auto', padding: '88px 24px' }}>
              <div style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto' }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.24em', textTransform: 'uppercase', color: 'var(--gold)' }}>
                  {sc.text('landing.products.eyebrow')}
                </div>
                <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(30px,4.4vw,50px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '12px 0 0', color: 'var(--cream)' }}>
                  {sc.text('landing.products.title')}
                </h2>
                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--muted)', margin: '14px auto 0', maxWidth: '52ch' }}>
                  {sc.text('landing.products.sub')}
                </p>
              </div>
              {asGrid ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 18, marginTop: 44 }}>
                  {productSlides.map((p, i) => (
                    <div
                      key={`${p.src}-${i}`}
                      className="asta-card"
                      style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                    >
                      <div style={{ aspectRatio: '4 / 3', background: 'linear-gradient(160deg,#241708,#160e06)', borderBottom: '1px solid var(--line)' }}>
                        <img
                          src={p.src}
                          alt={p.title}
                          loading="lazy"
                          style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', padding: 14 }}
                        />
                      </div>
                      <div style={{ padding: '16px 18px' }}>
                        {p.tag && (
                          <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--gold)' }}>
                            {p.tag}
                          </div>
                        )}
                        <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 18, letterSpacing: '.02em', margin: '3px 0 0', color: 'var(--cream)' }}>
                          {p.title}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <GalleryCarousel slides={productSlides} fit="contain" />
              )}
            </div>
          </section>
        );
      })()}

      {/* ── Gallery carousel (dark) ── */}
      <section id="gallery" style={{ scrollMarginTop: 80, background: 'linear-gradient(180deg,#160e06,#1d130a)', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '88px 24px' }}>
          <div style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto' }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.24em', textTransform: 'uppercase', color: 'var(--gold)' }}>
              {sc.text('landing.gallery.eyebrow')}
            </div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(30px,4.4vw,50px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '12px 0 0', color: 'var(--cream)' }}>
              {sc.text('landing.gallery.title')}
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--muted)', margin: '14px auto 0', maxWidth: '52ch' }}>
              {sc.text('landing.gallery.sub')}
            </p>
          </div>
          <GalleryCarousel
            slides={sc.items('landing.gallery.slides').map((s) => ({
              src: sc.src(s.imageKey ?? ''),
              tag: s.tag ?? '',
              title: s.title ?? '',
            }))}
          />
        </div>
      </section>

      {/* ── Ingredients (dark) ── */}
      <section id="ingredients" style={{ scrollMarginTop: 80, position: 'relative', background: 'linear-gradient(180deg,#160e06,#1d130a)', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '90px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.24em', textTransform: 'uppercase', color: 'var(--gold)' }}>
                {sc.text('landing.ingredients.eyebrow')}
              </div>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(30px,4.4vw,50px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '12px 0 0', color: 'var(--cream)' }}>
                {sc.text('landing.ingredients.title')}
              </h2>
            </div>
            <p style={{ maxWidth: '40ch', fontSize: 15, lineHeight: 1.7, color: 'var(--muted)', margin: 0 }}>
              {sc.text('landing.ingredients.lead')}
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 18, marginTop: 44 }}>
            {sc.items('landing.ingredients.items').map((ing, i) => (
              <div
                key={ing.name}
                className="asta-card"
                style={{ position: 'relative', background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 16, padding: '26px 24px', display: 'flex', flexDirection: 'column' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span
                    style={{
                      width: 54,
                      height: 54,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 'none',
                      background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,.08), rgba(0,0,0,.15))',
                      border: '1.5px solid var(--gold)',
                      color: INGREDIENT_META[i]?.accent ?? 'var(--gold)',
                      boxShadow: '0 0 0 4px rgba(229,185,75,.06)',
                    }}
                  >
                    {(() => {
                      const MIcon = INGREDIENT_META[i]?.Icon ?? Sparkles;
                      return <MIcon size={26} strokeWidth={1.7} />;
                    })()}
                  </span>
                  <div>
                    <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--gold)' }}>
                      {ing.tag}
                    </div>
                    <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 20, letterSpacing: '.02em', margin: '3px 0 0', color: 'var(--cream)' }}>
                      {ing.name}
                    </h3>
                  </div>
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.66, color: 'var(--muted)', margin: '16px 0 0', flex: 1 }}>{ing.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Astaxanthin feature (dark crimson) ── */}
      <section
        id="astaxanthin"
        style={{
          scrollMarginTop: 80,
          position: 'relative',
          overflow: 'hidden',
          background: 'radial-gradient(110% 80% at 78% 10%, #4a1116 0%, #2a0d10 40%, #170a08 100%)',
          borderTop: '1px solid var(--line)',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div
            className="asta-glow"
            style={{
              position: 'absolute',
              left: '80%',
              top: '22%',
              width: 520,
              height: 520,
              background: 'radial-gradient(circle, rgba(200,40,52,.35), transparent 62%)',
              animation: 'ac-glow 8s ease-in-out infinite',
            }}
          />
        </div>
        <div style={{ position: 'relative', maxWidth: 1200, margin: '0 auto', padding: '92px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 44, alignItems: 'center' }}>
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 9,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 600,
                  fontSize: 12.5,
                  letterSpacing: '.2em',
                  textTransform: 'uppercase',
                  color: '#F3B0B5',
                  border: '1px solid rgba(240,120,128,.3)',
                  borderRadius: 999,
                  padding: '8px 15px',
                }}
              >
                {sc.text('landing.astaxanthin.badge')}
              </div>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(32px,4.6vw,54px)', lineHeight: 1, letterSpacing: '.01em', textTransform: 'uppercase', margin: '20px 0 0', color: 'var(--cream)' }}>
                {sc.text('landing.astaxanthin.title')}
                <br />
                <span style={{ color: 'var(--gold)' }}>{sc.text('landing.astaxanthin.titleAccent')}</span>
              </h2>
              <p
                className="asta-accent-em"
                style={{ maxWidth: '52ch', fontSize: 16.5, lineHeight: 1.8, color: '#e7cfc8', margin: '20px 0 0' }}
              >
                {renderInline(sc.text('landing.astaxanthin.body'))}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 22 }}>
                {sc.paras('landing.astaxanthin.traits').map((t) => (
                  <span
                    key={t}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 7,
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: '#ead3cd',
                      background: 'rgba(255,255,255,.04)',
                      border: '1px solid rgba(240,120,128,.22)',
                      borderRadius: 999,
                      padding: '7px 13px',
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
              <a
                href="#gallery"
                onClick={scrollToAnchor('#gallery')}
                className="asta-btn-gold"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 9,
                  marginTop: 30,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 600,
                  fontSize: 14,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: '#2a1b0e',
                  background: 'var(--gold-grad)',
                  padding: '14px 24px',
                  borderRadius: 11,
                  boxShadow: '0 8px 22px rgba(200,148,42,.3)',
                }}
              >
                {sc.text('landing.astaxanthin.cta')} <ArrowRight size={15} strokeWidth={2.4} />
              </a>
            </div>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  position: 'relative',
                  borderRadius: 20,
                  overflow: 'hidden',
                  border: '1px solid rgba(240,120,128,.25)',
                  aspectRatio: '5/4',
                  background: 'linear-gradient(160deg,#3a0f13,#1c0a09)',
                  boxShadow: '0 26px 60px rgba(0,0,0,.5)',
                }}
              >
                <ImageSlot src={sc.img('landing.astaxanthin.image')} label="Astaxanthin visual coming soon" Icon={Sparkles} />
              </div>
              <div
                style={{
                  position: 'absolute',
                  WebkitBackdropFilter: 'blur(6px)',
                  backdropFilter: 'blur(6px)',
                  bottom: -18,
                  left: -18,
                  background: 'rgba(14,10,5,.82)',
                  border: '1px solid var(--line)',
                  borderRadius: 14,
                  padding: '16px 20px',
                  boxShadow: '0 12px 30px rgba(0,0,0,.4)',
                }}
              >
                <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--gold)' }}>
                  {sc.text('landing.astaxanthin.oracLabel')}
                </div>
                <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 30, color: 'var(--cream)', letterSpacing: '.01em', lineHeight: 1, marginTop: 4 }}>
                  {sc.text('landing.astaxanthin.oracValue')}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>{sc.text('landing.astaxanthin.oracUnit')}</div>
              </div>
            </div>
          </div>

          {/* Antioxidant comparison stats */}
          <div style={{ marginTop: 64 }}>
            <div style={{ textAlign: 'center', fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', color: '#F3B0B5' }}>
              {sc.text('landing.astaxanthin.statsHeading')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16, marginTop: 22 }}>
              {sc.items('landing.astaxanthin.stats').map((st) => (
                <div key={st.label} style={{ textAlign: 'center', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(240,120,128,.2)', borderRadius: 14, padding: '26px 16px' }}>
                  <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(34px,4vw,44px)', color: 'var(--gold)', lineHeight: 1 }}>{st.x}</div>
                  <div style={{ fontSize: 13, color: '#e7cfc8', marginTop: 8, lineHeight: 1.4 }}>{st.label}</div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--muted-2)', marginTop: 14 }}>
              {sc.text('landing.astaxanthin.statsNote')}
            </div>
          </div>

          {/* Clinical benefit tiles */}
          <div style={{ marginTop: 60 }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--gold)', textAlign: 'center' }}>
              {sc.text('landing.astaxanthin.benefitsHeading')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16, marginTop: 24 }}>
              {sc.items('landing.astaxanthin.benefits').map((b, i) => (
                <div key={b.title} className="asta-benefit" style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 14, padding: '24px 20px' }}>
                  <span style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(200,40,52,.14)', border: '1px solid rgba(240,120,128,.28)', color: '#F3B0B5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {(() => {
                      const BIcon = BENEFIT_ICONS[i] ?? Brain;
                      return <BIcon size={24} strokeWidth={1.8} />;
                    })()}
                  </span>
                  <h4 style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 17, letterSpacing: '.03em', margin: '16px 0 6px', color: 'var(--cream)' }}>{b.title}</h4>
                  <p style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--muted)', margin: 0 }}>{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How to Use (cream) ── */}
      <section id="how" style={{ scrollMarginTop: 96, background: 'var(--cream-2)', color: 'var(--espresso)', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '88px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 44, alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.24em', textTransform: 'uppercase', color: 'var(--gold-d)' }}>
              {sc.text('landing.how.eyebrow')}
            </div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(30px,4.4vw,50px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '12px 0 26px', color: 'var(--espresso)' }}>
              {sc.text('landing.how.title')}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {sc.items('landing.how.steps').map((s) => (
                <div key={s.n} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <span style={{ width: 38, height: 38, borderRadius: '50%', flex: 'none', background: 'var(--gold-grad)', color: '#2a1b0e', fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {s.n}
                  </span>
                  <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--espresso)', margin: '6px 0 0' }}>{s.text}</p>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 26, background: '#fff', border: '1px solid #efe4d2', borderLeft: '3px solid var(--gold-d)', borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--gold-d)', marginBottom: 6 }}>
                {sc.text('landing.how.cautionTitle')}
              </div>
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--muted-2)', margin: 0 }}>
                {sc.text('landing.how.cautionBody')}
              </p>
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ borderRadius: 20, overflow: 'hidden', border: '1px solid #efe4d2', aspectRatio: '1', boxShadow: '0 20px 44px rgba(90,60,20,.12)' }}>
              <ImageSlot src={sc.img('landing.how.image')} label="Lifestyle photo coming soon" Icon={Coffee} />
            </div>
          </div>
        </div>
      </section>

      {/* ── Certifications (dark) ── */}
      <section id="trust" style={{ scrollMarginTop: 80, background: 'linear-gradient(180deg,#1d130a,#160e06)', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '88px 24px' }}>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.24em', textTransform: 'uppercase', color: 'var(--gold)' }}>
              {sc.text('landing.certs.eyebrow')}
            </div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(30px,4.4vw,50px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '12px 0 0', color: 'var(--cream)' }}>
              {sc.text('landing.certs.title')}
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 20, marginTop: 46 }}>
            {sc.items('landing.certs.items').map((c, i) => (
              <div key={c.tag} style={{ display: 'flex', gap: 20, alignItems: 'center', background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 16, padding: 28 }}>
                <span
                  style={{
                    width: 88,
                    height: 88,
                    borderRadius: '50%',
                    flex: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    border: '2px solid var(--gold)',
                    color: 'var(--gold-l)',
                    background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,.06), rgba(0,0,0,.2))',
                    boxShadow: '0 0 0 5px rgba(229,185,75,.06)',
                  }}
                >
                  {(() => {
                    const CIcon = CERT_ICONS[i] ?? Check;
                    return <CIcon size={26} strokeWidth={1.9} />;
                  })()}
                  <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 12, letterSpacing: '.1em' }}>{c.tag}</span>
                </span>
                <div>
                  <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 20, letterSpacing: '.03em', margin: '0 0 3px', color: 'var(--cream)' }}>{c.title}</h3>
                  <div style={{ fontSize: 12.5, color: 'var(--gold)', fontWeight: 600, marginBottom: 8 }}>{c.by}</div>
                  <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--muted)', margin: 0 }}>{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ (cream) — hidden until at least one question exists, same rule as
             the Products carousel: an empty section would leave the header/footer
             anchors pointing at nothing. ── */}
      {faqs.length > 0 && (
        <section id="faq" style={{ scrollMarginTop: 96, background: 'var(--cream-2)', color: 'var(--espresso)', borderTop: '1px solid var(--line)' }}>
          <div style={{ maxWidth: 900, margin: '0 auto', padding: '88px 24px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 13, letterSpacing: '.24em', textTransform: 'uppercase', color: 'var(--gold-d)' }}>
                {sc.text('landing.faq.eyebrow')}
              </div>
              <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(30px,4.4vw,50px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '12px 0 0', color: 'var(--espresso)' }}>
                {sc.text('landing.faq.title')}
              </h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 42 }}>
              {faqs.map((f, i) => (
                <details
                  key={f.q || i}
                  className="asta-faq"
                  style={{ background: '#fff', border: '1px solid #efe4d2', borderRadius: 14, padding: '4px 20px' }}
                >
                  {/* The question is an <h3> like every other repeated card
                      title on this page (blend pillars, ingredients, certs), so
                      the FAQ shows up in a screen reader's heading list instead
                      of being a flat run of summaries. */}
                  <summary
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      cursor: 'pointer',
                      listStyle: 'none',
                      padding: '18px 0',
                    }}
                  >
                    <h3
                      style={{
                        flex: 1,
                        margin: 0,
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 600,
                        fontSize: 17,
                        letterSpacing: '.01em',
                        color: 'var(--espresso)',
                      }}
                    >
                      {f.q}
                    </h3>
                    <ChevronDown className="asta-faq-chev" size={19} strokeWidth={2.2} style={{ flex: 'none', color: 'var(--gold-d)' }} />
                  </summary>
                  {/* renderLines, not a bare string: the answer is edited in a
                      multi-line <Textarea>, so typed line breaks must survive. */}
                  <p style={{ fontSize: 15.5, lineHeight: 1.7, color: 'var(--muted-2)', margin: '0 0 20px', paddingRight: 33 }}>
                    {renderLines(f.a ?? '')}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA band ── */}
      <section id="contact" style={{ scrollMarginTop: 80, position: 'relative', overflow: 'hidden', background: 'radial-gradient(120% 120% at 50% 0%, #2c1c0d, #150d06)', borderTop: '1px solid var(--line)' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div
            className="asta-glow"
            style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              width: 700,
              height: 500,
              background: 'radial-gradient(circle, rgba(200,148,42,.22), transparent 65%)',
              animation: 'ac-glow 9s ease-in-out infinite',
            }}
          />
        </div>
        <div style={{ position: 'relative', maxWidth: 900, margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
          <div style={{ fontFamily: FONT_SCRIPT, fontSize: 'clamp(38px,6vw,58px)', color: 'var(--gold-l)', lineHeight: 1 }}>
            {sc.text('landing.cta.script')}
          </div>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(34px,5vw,60px)', letterSpacing: '.01em', textTransform: 'uppercase', margin: '4px 0 0', color: 'var(--cream)' }}>
            {sc.text('landing.cta.title')}
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.75, color: 'var(--muted)', maxWidth: '52ch', margin: '20px auto 0' }}>
            {sc.text('landing.cta.body')}
          </p>
          {/* This section IS the "Where to Buy" destination — the header pill and
              the footer link both scroll here — so it carries the marketplace
              buttons, and the email is the fallback for anyone who'd rather ask a
              distributor. A row with no URL is skipped and an emptied contact
              email hides its button: a dead button is worse than none on the
              site's only call to action. */}
          {(() => {
            const shops = sc
              .items('landing.cta.shops')
              .map((s) => ({
                label: s.label ?? '',
                url: (s.url ?? '').trim(),
                logo: s.imageKey ? sc.src(s.imageKey) : '',
              }))
              .filter((s) => s.url && s.label);
            if (shops.length === 0) return null;
            return (
              <div style={{ marginTop: 34 }}>
                <div
                  style={{
                    fontFamily: FONT_DISPLAY,
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: '.22em',
                    textTransform: 'uppercase',
                    color: 'var(--gold)',
                  }}
                >
                  {sc.text('landing.cta.shopsTitle')}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    justifyContent: 'center',
                    marginTop: 14,
                  }}
                >
                  {shops.map((s) => (
                    <a
                      key={s.url}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="asta-card"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 10,
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 600,
                        fontSize: 14,
                        letterSpacing: '.04em',
                        color: 'var(--cream)',
                        background: 'rgba(255,255,255,.05)',
                        border: '1px solid rgba(200,148,42,.42)',
                        padding: '10px 18px 10px 12px',
                        borderRadius: 11,
                        textDecoration: 'none',
                      }}
                    >
                      {/* Each mark carries its own background (Shopee white,
                          Lazada gradient, TikTok black), so a light chip keeps
                          all three legible on the espresso band. */}
                      {s.logo ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            background: '#fff',
                            overflow: 'hidden',
                          }}
                        >
                          <img
                            src={s.logo}
                            alt=""
                            loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </span>
                      ) : null}
                      {s.label}
                      <ArrowRight size={14} strokeWidth={2.4} />
                    </a>
                  ))}
                </div>
              </div>
            );
          })()}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', marginTop: 32 }}>
            {contactEmail && (
            <a
              href={`mailto:${contactEmail}`}
              className="asta-btn-gold"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 9,
                fontFamily: FONT_DISPLAY,
                fontWeight: 600,
                fontSize: 15,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: '#2a1b0e',
                background: 'var(--gold-grad)',
                padding: '15px 28px',
                borderRadius: 11,
                boxShadow: '0 8px 22px rgba(200,148,42,.32)',
              }}
            >
              {sc.text('landing.cta.ctaSecondary')} <ArrowRight size={15} strokeWidth={2.4} />
            </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
